<template>
  <b-card no-body class="dash-panel dash-chart-panel">
    <b-card-header class="dash-panel__header border-0 pb-0">
      <div class="dash-panel__heading">
        <span class="dash-panel__icon dash-panel__icon--yearly">
          <feather-icon icon="ActivityIcon" size="18" />
        </span>
        <div>
          <b-card-title class="dash-panel__title mb-0">{{
            $t("Yearly Summary")
          }}</b-card-title>
          <small class="dash-panel__subtitle">{{
            $t("Paid vs unpaid by month")
          }}</small>
        </div>
      </div>
    </b-card-header>

    <b-card-body class="pb-1">
      <div ref="chartViewport" class="dash-chart-viewport-inner">
      <div class="dash-chart-canvas dash-chart-canvas--tall">
        <div v-if="!chartReady" class="dash-chart-empty text-muted text-center py-5">
          {{ $t("Loading") }}...
        </div>
        <div v-else-if="!chartInView" class="dash-chart-placeholder dash-chart-placeholder--tall">
          <span class="dash-chart-line-preview">
            <span v-for="n in 8" :key="n" class="dash-chart-line-preview__bar" />
          </span>
        </div>
        <vue-apex-charts
          v-else
          :key="chartRenderKey()"
          type="area"
          height="380"
          :options="chartOptions"
          :series="chartSeries"
        />
      </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardBody, BCardHeader, BCardTitle } from "bootstrap-vue";
import VueApexCharts from "vue-apexcharts";
import chartInViewMixin from "@/views/dashboard/ecommerce/mixins/chartInViewMixin";
import { dashApexTooltipShared } from "@/views/dashboard/ecommerce/utils/dashboardApexTooltip";

export default {
  mixins: [chartInViewMixin],
  components: {
    VueApexCharts,
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
  },
  props: {
    yearlyData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      chartSeries: [
        { name: "Paid", data: [] },
        { name: "Unpaid", data: [] },
      ],
      chartOptions: {},
    };
  },
  computed: {
    chartReady() {
      return (
        this.yearlyData &&
        Array.isArray(this.yearlyData.data) &&
        this.yearlyData.data.length > 0
      );
    },
  },
  watch: {
    yearlyData: {
      deep: true,
      immediate: true,
      handler(val) {
        this.applyYearlyData(val);
      },
    },
  },
  methods: {
    applyYearlyData(yd) {
      if (!yd || !Array.isArray(yd.data)) return;

      const paid = [];
      const unpaid = [];
      yd.data.forEach((el) => {
        paid.push(Number(el.collection) || 0);
        unpaid.push(Number(el.dues) || 0);
      });

      const peak = Math.max(...paid, ...unpaid, 0);
      const yMax =
        Number(yd.highestDues) > 0
          ? Number(yd.highestDues)
          : peak > 0
            ? Math.ceil(peak * 1.12)
            : 1000;

      this.chartSeries = [
        { name: "Paid", data: paid },
        { name: "Unpaid", data: unpaid },
      ];

      this.chartOptions = {
        chart: {
          type: "area",
          toolbar: { show: false },
          fontFamily: "inherit",
          animations: this.apexStaggerAnimation(100),
          zoom: { enabled: false },
        },
        colors: ["#22c55e", "#ef4444"],
        stroke: {
          curve: "smooth",
          width: 3,
          lineCap: "round",
        },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 0.4,
            opacityFrom: 0.55,
            opacityTo: 0.06,
            stops: [0, 90, 100],
          },
        },
        markers: {
          size: 0,
          strokeWidth: 0,
          hover: { size: 6, sizeOffset: 2 },
        },
        dataLabels: { enabled: false },
        grid: {
          borderColor: "#f1f5f9",
          strokeDashArray: 4,
          padding: { left: 8, right: 12 },
        },
        xaxis: {
          categories: [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ],
          axisBorder: { show: false },
          labels: {
            style: { colors: "#64748b", fontSize: "11px", fontWeight: 600 },
          },
        },
        yaxis: {
          min: 0,
          max: yMax,
          tickAmount: 5,
          labels: {
            style: { colors: "#64748b", fontSize: "11px" },
            formatter(val) {
              if (val >= 1e6) return `${(val / 1e6).toFixed(1)}M`;
              if (val >= 1e3) return `${(val / 1e3).toFixed(0)}k`;
              return Math.round(val);
            },
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "right",
          fontWeight: 600,
          fontSize: "12px",
          markers: { width: 8, height: 8, radius: 12 },
        },
        tooltip: dashApexTooltipShared(),
      };

      this.bumpChartData();
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.dash-panel__icon--yearly {
  background: #ecfdf5;
  color: #16a34a;
}

.dash-chart-canvas--tall {
  min-height: 360px;
  height: 380px;
}

.dash-chart-empty {
  font-size: 0.9rem;
  font-weight: 600;
}

.dash-chart-placeholder--tall {
  min-height: 380px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 2.5rem;
}

.dash-chart-line-preview {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  height: 120px;
}

.dash-chart-line-preview__bar {
  width: 14px;
  border-radius: 6px 6px 2px 2px;
  background: linear-gradient(180deg, #fca5a5, #ef4444);
  animation: dashLinePreview 1.1s ease-in-out infinite;
  transform-origin: bottom;

  @for $i from 1 through 8 {
    &:nth-child(#{$i}) {
      animation-delay: #{$i * 0.08}s;
      height: #{40 + $i * 8}px;
    }
  }
}

@keyframes dashLinePreview {
  0%,
  100% {
    transform: scaleY(0.25);
    opacity: 0.4;
  }
  50% {
    transform: scaleY(1);
    opacity: 1;
  }
}
</style>
